/**
 * Container-relative geometry for the editor canvas chrome (selection rings and
 * the hover drag handle), expressed in the preview container's scroll
 * coordinates.
 */
export interface OverlayRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Viewport-space sides of a rect, as clip-window math works with. */
export interface ViewportSides {
  top: number;
  left: number;
  right: number;
  bottom: number;
}

/**
 * The CSS clip-path pinning a frame inside a clip window: an inset() whose
 * sides cut away whatever part of the frame falls outside the window. A side
 * the window does not constrain (or an unclipped infinite axis) insets by
 * zero, so a frame fully inside its window comes back effectively unclipped.
 * @param frame - The clipped element's border box, in viewport coordinates
 * @param window - The clip window, in the same coordinates
 * @returns The inset() clip-path value
 */
export function surfaceClipPath(
  frame: ViewportSides,
  window: ViewportSides,
): string {
  const top = Math.max(0, window.top - frame.top);
  const left = Math.max(0, window.left - frame.left);
  const right = Math.max(0, frame.right - window.right);
  const bottom = Math.max(0, frame.bottom - window.bottom);
  return `inset(${top}px ${right}px ${bottom}px ${left}px)`;
}

/** Width of the gutter strip the hover drag handle occupies, in px. */
export const DRAG_HANDLE_GUTTER_PX = 24;

/**
 * Places the hover drag handle in the gutter immediately left of a block,
 * spanning the block's full height so the pointer can travel from anywhere on
 * the block's left edge onto the handle without crossing unhovered canvas. A
 * block sitting against the container's left edge keeps its handle on-canvas.
 * @param block - The hovered block's container-relative rect
 * @returns The handle strip's container-relative rect
 */
export function dragHandleRect(block: OverlayRect): OverlayRect {
  return {
    top: block.top,
    left: Math.max(0, block.left - DRAG_HANDLE_GUTTER_PX),
    width: DRAG_HANDLE_GUTTER_PX,
    height: block.height,
  };
}

/** A viewport point at one caret end of a captured native selection. */
export interface SelectionPoint {
  x: number;
  y: number;
}

/** The native selection a click gesture left, as viewport caret points. */
export interface CapturedSelection {
  anchor: SelectionPoint;
  head: SelectionPoint;
}

/**
 * Captures the native selection the opening click gesture left on a rendered
 * block, as viewport points of its anchor and head carets - a collapsed caret
 * for a plain click, a range for a click-drag. The edit surface is pinned over
 * the block with matched appearance, so these points map straight into the
 * session's view. An end that overshot the block (a drag past its edge) is
 * clamped to the block's box; a selection with no end inside the block is
 * stale and ignored.
 * @param block - The rendered block element entering the edit session
 * @returns The captured selection, or null when there is none to restore
 */
export function captureSelectionPoints(
  block: HTMLElement,
): CapturedSelection | null {
  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return null;
  }
  const { anchorNode, anchorOffset, focusNode, focusOffset } = selection;
  if (!anchorNode || !focusNode) {
    return null;
  }
  if (!block.contains(anchorNode) && !block.contains(focusNode)) {
    return null;
  }
  const anchor = selectionEndPoint(anchorNode, anchorOffset, block);
  const head = selectionEndPoint(focusNode, focusOffset, block);
  return anchor && head ? { anchor, head } : null;
}

/**
 * The viewport point of one selection end: its caret rect when the end sits
 * inside the block, or the nearest block corner when the drag overshot it.
 * @param node - The selection end's node
 * @param offset - The selection end's offset within the node
 * @param block - The rendered block bounding the session
 * @returns The caret point, or null when the caret has no geometry
 */
function selectionEndPoint(
  node: Node,
  offset: number,
  block: HTMLElement,
): SelectionPoint | null {
  if (!block.contains(node)) {
    return blockEdgePoint(block, node);
  }
  const range = document.createRange();
  range.setStart(node, offset);
  range.collapse(true);
  const rect = range.getClientRects()[0] ?? range.getBoundingClientRect();
  if (
    rect.width === 0 &&
    rect.height === 0 &&
    rect.top === 0 &&
    rect.left === 0
  ) {
    return null;
  }
  return { x: rect.left, y: rect.top + rect.height / 2 };
}

/**
 * The block corner standing in for a selection end outside the block: the top
 * left when the end precedes the block, the bottom right when it follows.
 * @param block - The rendered block bounding the session
 * @param node - The out-of-block selection end's node
 * @returns The clamped viewport point
 */
function blockEdgePoint(block: HTMLElement, node: Node): SelectionPoint {
  const rect = block.getBoundingClientRect();
  const blockFollowsNode =
    (node.compareDocumentPosition(block) & Node.DOCUMENT_POSITION_FOLLOWING) !==
    0;
  return blockFollowsNode
    ? { x: rect.left, y: rect.top + 1 }
    : { x: rect.right, y: rect.bottom - 1 };
}

/** The horizontal span of a block's rendered column, in client coordinates. */
export interface HitTestColumn {
  left: number;
  width: number;
}

/**
 * Picks the X to hit-test a live handle drag at. The grip a drag starts from
 * sits in the gutter beside the block, so a straight vertical drag keeps the
 * pointer off every block's rendered column and would resolve no drop
 * candidate at all. Pulling the X back into the dragged block's column - and
 * clear of the gutter strip the grip can occupy - keeps a block under the
 * probe for the whole drag, while the caller's live pointer Y still decides
 * which block and which side of it. A pointer already inside the column is
 * used as-is, so dragging across page content still resolves what is under it.
 * @param pointerX - The live pointer X, in client coordinates
 * @param column - The dragged block's column, in the same coordinates
 * @returns The X to hit-test at, inside the column
 */
export function dropHitTestX(pointerX: number, column: HitTestColumn): number {
  const min = column.left + DRAG_HANDLE_GUTTER_PX;
  const max = column.left + column.width - DRAG_HANDLE_GUTTER_PX;
  if (min >= max) {
    return column.left + column.width / 2;
  }
  return Math.min(Math.max(pointerX, min), max);
}

/**
 * Compares two overlay rects (either of which may be absent) for exact
 * geometric equality, so a tracking loop only re-renders when something moved.
 * @param a - The previous rect, or null
 * @param b - The next rect, or null
 * @returns True when both are absent or both describe the same rectangle
 */
export function rectEquals(
  a: OverlayRect | null,
  b: OverlayRect | null,
): boolean {
  if (a === null || b === null) {
    return a === b;
  }
  return (
    a.top === b.top &&
    a.left === b.left &&
    a.width === b.width &&
    a.height === b.height
  );
}

/**
 * Compares two overlay rect lists for exact geometric equality in order.
 * @param a - The previous rect list
 * @param b - The next rect list
 * @returns True when both lists have identical rects in the same order
 */
export function rectsEqual(a: OverlayRect[], b: OverlayRect[]): boolean {
  return (
    a.length === b.length &&
    a.every((rect, index) => rectEquals(rect, b[index]))
  );
}
